import { Dispatch, SetStateAction, useState, useEffect, useRef } from 'react';

import { getSecureItem, setSecureItem } from '../utils/secure-storage';

export function useStoredState<T>(name: string, initialValue: T): [T, Dispatch<SetStateAction<T>>, boolean] {
    const [stateValue, setStateValue] = useState<T>(initialValue);
    const [isLoaded, setIsLoaded] = useState(false);
    const isLoadingRef = useRef(false);

    const retrieveValue = async () => {
        if(isLoadingRef.current) return;

        isLoadingRef.current = true;

        try {
            const storedItem = await getSecureItem<T>(name);

            if(storedItem !== null) {
                setStateValue(storedItem);
            } else {
                await setSecureItem(name, initialValue);
                setStateValue(initialValue);
            }
        } catch(_) {
            await setSecureItem(name, initialValue);
            setStateValue(initialValue);
        }

        setIsLoaded(true);
        isLoadingRef.current = false;
    };

    const setState = (value: SetStateAction<T>) => {
        const newValue = typeof value === 'function' ? (value as (prevState: T) => T)(stateValue) : value;

        setSecureItem(name, newValue).then(() => {
            setStateValue(newValue);

            window.dispatchEvent(new CustomEvent('localStorageChange', {
                detail: name,
            }));
        });
    };

    useEffect(() => {
        const changeState = (event: any) => {
            if(event.key === name) {
                retrieveValue().then();
            }
        };

        const changeStateLocal = (event: any) => {
            if(event.detail === name) {
                retrieveValue().then();
            }
        };

        window.addEventListener('storage', changeState);
        window.addEventListener('localStorageChange', changeStateLocal);

        retrieveValue().then();

        return () => {
            window.removeEventListener('storage', changeState);
            window.removeEventListener('localStorageChange', changeStateLocal);
        };
    }, [name]);

    return [stateValue, setState, isLoaded];
}
